/*
 * One WebGL particle field behind the whole services page (and each service
 * page, with its own looks from looks.js). Every section has a formation
 * (formations.js) and the particles morph from one to the next as the next
 * section scrolls in, so the scene itself is the transition between sections.
 *
 * All motion runs in the vertex shader: per frame the CPU only sets uniforms.
 * Formations are uploaded once as buffers and swapped by binding. It is plain
 * WebGL (one program, one draw call), so the page doesn't ship three.js.
 */
import { FORMATIONS, random } from './formations';

const FOV = 40;
const CAMERA_Z = 6;
// How far (in viewport heights) the next section scrolls in while the morph runs
const MORPH_SPAN = 0.7;

/*
 * Look of each section. colors: two particle colours (Adam palette);
 * fx: [scan sweep, flow pulse, ripple, multicolour]; tilt: base X rotation;
 * spin: Y rotation speed; side: 1 places the shape beside left-aligned text;
 * face: 1 turns flat shapes (a padlock, a browser window) to the viewer, where
 * they sway instead of spinning; size: scale of the shape (default 1).
 */
export const LOOKS = {
    hero:          { shape: 'globe',   colors: [0x718cff, 0xeefcff], fx: [0, 0.35, 0, 0], tilt: 0.25, spin: 0.06, side: 0, glow: 0.55 },
    ai:            { shape: 'neural',  colors: [0x62f4e5, 0x718cff], fx: [0, 0.8, 0, 0],  tilt: 0.1,  spin: 0.05, side: 1, glow: 1 },
    engineering:   { shape: 'lattice', colors: [0xeefcff, 0x8ea2c8], fx: [0.6, 0, 0, 0],  tilt: 0.5,  spin: 0.07, side: 1, glow: 0.9 },
    cybersecurity: { shape: 'shield',  colors: [0x5cf2b0, 0x62f4e5], fx: [1, 0, 0, 0],    tilt: 0.15, spin: 0.08, side: 1, glow: 1 },
    talent:        { shape: 'orbits',  colors: [0xb18cff, 0xff8fc7], fx: [0, 0.6, 0, 0],  tilt: 0.2,  spin: 0.1,  side: 1, glow: 1 },
    automation:    { shape: 'flow',    colors: [0xffc56b, 0xff9a62], fx: [0, 1, 0, 0],    tilt: 0.35, spin: 0.06, side: 1, glow: 1 },
    innovation:    { shape: 'wave',    colors: [0xff8fc7, 0x62f4e5], fx: [0, 0, 1, 1],    tilt: 0.55, spin: 0.03, side: 1, glow: 1 },
    design:        { shape: 'reel',    colors: [0xd8ff70, 0xfff1c9], fx: [0, 1, 0, 0.35], tilt: 0.45, spin: 0.07, side: 1, glow: 1 },
    contact:       { shape: 'globe',   colors: [0x718cff, 0x62f4e5], fx: [0, 0.35, 0, 0], tilt: 0.25, spin: 0.06, side: 0, glow: 0.6 },
};

const vertexShader = /* glsl */`
    precision highp float;

    attribute vec4 aFrom;
    attribute vec4 aTo;
    attribute vec4 aSeed;

    uniform mat4  modelViewMatrix;
    uniform mat4  projectionMatrix;
    uniform float uMix;
    uniform float uTime;
    uniform float uScale;
    uniform float uSize;
    uniform vec2  uPointer;
    uniform vec3  uColorA0;
    uniform vec3  uColorB0;
    uniform vec3  uColorA1;
    uniform vec3  uColorB1;
    uniform vec4  uFx0;
    uniform vec4  uFx1;

    varying vec3  vColor;
    varying float vAlpha;

    vec3 drift(vec3 p, float t, float s) {
        return vec3(
            sin(p.y * 2.1 + t * 0.9 + s * 6.28),
            sin(p.z * 2.3 + t * 1.1 + s * 4.1),
            sin(p.x * 1.9 + t * 0.7 + s * 5.3)
        );
    }

    void main() {
        // Staggered per particle, so the shape dissolves and reforms in waves
        float m = smoothstep(0.0, 1.0, clamp(uMix * 1.5 - aSeed.y * 0.5, 0.0, 1.0));
        vec4 fx = mix(uFx0, uFx1, m);
        vec3 p = mix(aFrom.xyz, aTo.xyz, m);
        float phase = mix(aFrom.w, aTo.w, m);
        float t = uTime;

        // Mid-morph the particles bloom outward, then settle into the new shape
        float bloom = sin(m * 3.14159);
        p += drift(p, t * 0.6, aSeed.z) * (0.025 + bloom * 0.45);
        p *= 1.0 + bloom * 0.12;

        // Innovation: the surface ripples
        p.y += sin(p.x * 2.4 + t * 1.2) * cos(p.z * 2.0 - t * 0.8) * 0.16 * fx.z;

        vec4 mv = modelViewMatrix * vec4(p, 1.0);
        vec4 clip = projectionMatrix * mv;

        // Particles part around the pointer
        vec2 ndc = clip.xy / clip.w;
        vec2 away = ndc - uPointer;
        float near = 1.0 - smoothstep(0.0, 0.28, length(away));
        clip.xy += normalize(away + 1e-4) * near * 0.06 * clip.w;

        gl_Position = clip;
        gl_PointSize = max(uSize * aSeed.w * uScale / -mv.z, 1.0);

        // Effects that travel through the shape
        float flow = pow(0.5 + 0.5 * sin((phase - t * 0.12) * 37.7), 8.0) * fx.y;
        float scan = smoothstep(0.06, 0.0, abs(fract(t * 0.12) - phase)) * fx.x;
        float hue = fract(phase + t * 0.03);
        vec3 colorA = mix(uColorA0, uColorA1, m);
        vec3 colorB = mix(uColorB0, uColorB1, m);
        vec3 base = mix(colorA, colorB, aSeed.x);
        vec3 rainbow = 0.55 + 0.45 * cos(6.28318 * (hue + vec3(0.0, 0.33, 0.67)));
        vColor = mix(base, rainbow, fx.w * 0.6) * (1.0 + flow * 1.1 + scan * 1.5 + near * 0.8);
        vAlpha = (0.38 + aSeed.x * 0.3) * (1.0 - bloom * 0.35);
    }
`;

// Same soft glow as the Adam scenes (src/js/adam/gl.js)
const fragmentShader = /* glsl */`
    precision mediump float;

    uniform float uOpacity;
    varying vec3  vColor;
    varying float vAlpha;

    void main() {
        float d = length(gl_PointCoord - 0.5);
        float glow = pow(smoothstep(0.5, 0.0, d), 1.7);
        float core = smoothstep(0.16, 0.0, d);
        float a = (glow + core * 0.5) * vAlpha * uOpacity;
        if (a < 0.004) discard;
        gl_FragColor = vec4(vColor + core * 0.3, a);
    }
`;

function particleCount() {
    const small = window.innerWidth < 900;
    const weak = (navigator.hardwareConcurrency || 8) <= 4 || (navigator.deviceMemory || 8) <= 4 || navigator.connection?.saveData;
    return Math.round((small ? 3200 : 7000) * (weak ? 0.6 : 1));
}

// Hex sRGB → linear RGB, the space the shaders blend in
function linear(hex) {
    return [16, 8, 0].map(shift => {
        const c = ((hex >> shift) & 255) / 255;
        return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
}

function compile(gl) {
    const program = gl.createProgram();
    [[gl.VERTEX_SHADER, vertexShader], [gl.FRAGMENT_SHADER, fragmentShader]].forEach(([type, source]) => {
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader));
        gl.attachShader(program, shader);
    });
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program));
    return program;
}

// Column-major camera-space transform: translate(x, 0, -z) · rotX · rotY · scale
function modelView(out, x, rx, ry, s) {
    const cx = Math.cos(rx), sx = Math.sin(rx), cy = Math.cos(ry), sy = Math.sin(ry);
    out.set([
        cy * s, sx * sy * s, -cx * sy * s, 0,
        0, cx * s, sx * s, 0,
        sy * s, -sx * cy * s, cx * cy * s, 0,
        x, 0, -CAMERA_Z, 1,
    ]);
}

function perspective(out, aspect, near = 0.1, far = 50) {
    const f = 1 / Math.tan((FOV / 2) * Math.PI / 180);
    out.fill(0);
    out[0] = f / aspect;
    out[5] = f;
    out[10] = (far + near) / (near - far);
    out[11] = -1;
    out[14] = (2 * far * near) / (near - far);
}

/**
 * Mounts the field into `canvas`, with a look per [data-universe] section.
 * Returns false when WebGL is unavailable, so the page keeps its CSS backdrop.
 */
export function initField(canvas, table = LOOKS) {
    const gl = canvas.getContext('webgl', {
        alpha: true, antialias: false, depth: false, powerPreference: 'high-performance',
    });
    let program;
    try {
        program = gl && compile(gl);
    } catch (error) {
        console.warn('Services field disabled:', error);
    }
    if (!program) return false;

    gl.useProgram(program);
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE, gl.ONE, gl.ONE); // additive glow
    gl.clearColor(0, 0, 0, 0);

    let pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);

    const sections = Array.from(document.querySelectorAll('[data-universe]'))
        .filter(el => table[el.dataset.universe]);
    const looks = sections.map(el => table[el.dataset.universe]);
    if (looks.length < 2) return false;

    // ── Buffers: one seed set, one buffer per formation ─────────────────────
    let count = particleCount();
    const rnd = random(7);
    const seed = new Float32Array(count * 4);
    for (let i = 0; i < count; i++) {
        seed[i * 4]     = rnd();                 // colour blend
        seed[i * 4 + 1] = rnd();                 // morph delay
        seed[i * 4 + 2] = rnd();                 // drift phase
        seed[i * 4 + 3] = 0.5 + rnd() * rnd() * 2; // size, mostly small
    }

    // Formations fill in order (sphere, then ring...); scattering them over the
    // particles keeps every shape whole when fewer particles are drawn
    const slot = Array.from({ length: count }, (_, i) => i);
    for (let i = count - 1; i > 0; i--) {
        const j = Math.floor(rnd() * (i + 1));
        [slot[i], slot[j]] = [slot[j], slot[i]];
    }

    function buffer(data) {
        const buf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
        return buf;
    }

    // Formations are built the first time a section needs them
    const shapes = {};
    function shape(name) {
        if (shapes[name]) return shapes[name];
        const data = new Float32Array(count * 4);
        const fill = FORMATIONS[name](count, random(Object.keys(FORMATIONS).indexOf(name) + 11));
        const out = [0, 0, 0, 0];
        for (let i = 0; i < count; i++) {
            fill(i, out);
            data.set(out, slot[i] * 4);
        }
        return (shapes[name] = buffer(data));
    }

    function attribute(name, buf) {
        const location = gl.getAttribLocation(program, name);
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.enableVertexAttribArray(location);
        gl.vertexAttribPointer(location, 4, gl.FLOAT, false, 0, 0);
    }

    attribute('aSeed', buffer(seed));

    const u = {};
    const uniform = name => u[name] ?? (u[name] = gl.getUniformLocation(program, name));
    const mvMatrix = new Float32Array(16);
    const projMatrix = new Float32Array(16);

    let pair = -1;
    function setPair(i) {
        if (i === pair) return;
        pair = i;
        const from = looks[i];
        const to = looks[Math.min(i + 1, looks.length - 1)];
        attribute('aFrom', shape(from.shape));
        attribute('aTo', shape(to.shape));
        gl.uniform3fv(uniform('uColorA0'), linear(from.colors[0]));
        gl.uniform3fv(uniform('uColorB0'), linear(from.colors[1]));
        gl.uniform3fv(uniform('uColorA1'), linear(to.colors[0]));
        gl.uniform3fv(uniform('uColorB1'), linear(to.colors[1]));
        gl.uniform4fv(uniform('uFx0'), from.fx);
        gl.uniform4fv(uniform('uFx1'), to.fx);
    }

    // ── Layout ───────────────────────────────────────────────────────────────
    let width = 1, height = 1, halfW = 1, wide = true;
    let tops = [];

    function measure() {
        tops = sections.map(el => el.getBoundingClientRect().top + window.scrollY);
    }

    function resize() {
        width = canvas.clientWidth || window.innerWidth;
        height = canvas.clientHeight || window.innerHeight;
        canvas.width = Math.round(width * pixelRatio);
        canvas.height = Math.round(height * pixelRatio);
        gl.viewport(0, 0, canvas.width, canvas.height);

        const aspect = width / height;
        perspective(projMatrix, aspect);
        gl.uniformMatrix4fv(uniform('projectionMatrix'), false, projMatrix);
        const tan = Math.tan((FOV / 2) * Math.PI / 180);
        halfW = tan * CAMERA_Z * aspect;
        wide = aspect > 1.15 && width >= 900;
        gl.uniform1f(uniform('uScale'), height * pixelRatio / (2 * tan));
        measure();
    }

    new ResizeObserver(resize).observe(canvas);
    // Sections move when content above them changes height (fonts, FAQ answers)
    new ResizeObserver(measure).observe(document.querySelector('main') || document.body);
    resize();

    // ── Scroll → morph position ──────────────────────────────────────────────
    function target() {
        const vh = window.innerHeight;
        const y = window.scrollY + vh;
        let stop = 0;
        for (let i = 1; i < tops.length; i++) {
            stop += Math.min(1, Math.max(0, (y - tops[i]) / (vh * MORPH_SPAN)));
        }
        return stop;
    }

    // ── Pointer ──────────────────────────────────────────────────────────────
    const pointer = { x: 0, y: 0, tx: 0, ty: 0, active: false };
    window.addEventListener('pointermove', e => {
        if (e.pointerType !== 'mouse') return;
        pointer.active = true;
        pointer.tx = (e.clientX / width) * 2 - 1;
        pointer.ty = -(e.clientY / height) * 2 + 1;
    }, { passive: true });
    document.documentElement.addEventListener('pointerleave', () => { pointer.active = false; });

    // ── Loop ─────────────────────────────────────────────────────────────────
    let stop = target();
    let yaw = 0;
    let time = 0;
    let last = 0;
    let slowFrames = 0;
    let frames = 0;
    let tier = 0;
    let lost = false;
    const FRAME_MS = 1000 / 61;

    canvas.addEventListener('webglcontextlost', event => {
        event.preventDefault();
        lost = true;
        document.body.classList.remove('has-field');
    });

    function frame(now) {
        if (lost) return;
        requestAnimationFrame(frame);
        if (now - last < FRAME_MS) return; // cap at ~60fps on high-refresh screens
        const dt = last ? Math.min((now - last) / 1000, 0.05) : 1 / 60;
        last = now;

        // Every 2s, if a third of the frames ran under ~40fps, cut the pixel and
        // particle cost (twice at most). Formations are shuffled over the
        // particles, so drawing fewer keeps every shape whole.
        if (dt > 0.024) slowFrames++;
        if (++frames === 120) {
            if (slowFrames > 40 && tier < 2) {
                tier++;
                pixelRatio = tier === 1 ? 1 : 0.75;
                count = Math.round(count * 0.65);
                resize();
            }
            frames = slowFrames = 0;
        }

        const ease = 1 - Math.exp(-dt * 5);
        stop += (target() - stop) * ease;
        const i = Math.min(Math.floor(stop), looks.length - 2);
        const mix = Math.max(0, Math.min(1, stop - i));
        setPair(Math.max(0, i));

        const from = looks[pair];
        const to = looks[Math.min(pair + 1, looks.length - 1)];
        const k = mix * mix * (3 - 2 * mix);
        const lerp = (a, b) => a + (b - a) * k;

        pointer.x += (pointer.tx - pointer.x) * 0.08;
        pointer.y += (pointer.ty - pointer.y) * 0.08;

        // Spin, or for facing shapes ease back to the front and sway. The yaw is
        // integrated, so blending between the two never jumps.
        const face = lerp(from.face || 0, to.face || 0);
        yaw += dt * lerp(from.spin, to.spin) * (1 - face);
        yaw += (Math.round(yaw / (Math.PI * 2)) * Math.PI * 2 - yaw) * face * (1 - Math.exp(-dt * 1.5));
        const ry = yaw + face * Math.sin(time * 0.3) * 0.35 + pointer.x * 0.12;

        // Beside left-aligned text on wide screens, centred and dimmer otherwise
        const side = wide ? lerp(from.side, to.side) : 0;
        const scale = Math.min(1, (wide ? halfW * 0.42 : halfW * 0.95) / 2.1) * lerp(from.size || 1, to.size || 1);
        modelView(mvMatrix, side * halfW * 0.5, lerp(from.tilt, to.tilt) + pointer.y * 0.06, ry, scale);

        // Recede while the section's cards are being read, return for the morph
        const into = (window.scrollY - (tops[pair] ?? 0)) / window.innerHeight;
        const reading = Math.max(0, Math.min(1, (into - 0.1) / 0.4));
        const dim = 1 - 0.5 * reading * reading * (3 - 2 * reading) * (1 - k);

        time += dt;
        gl.uniformMatrix4fv(uniform('modelViewMatrix'), false, mvMatrix);
        gl.uniform1f(uniform('uMix'), mix);
        gl.uniform1f(uniform('uTime'), time);
        gl.uniform1f(uniform('uSize'), 0.03 * Math.max(0.6, scale));
        gl.uniform1f(uniform('uOpacity'), lerp(from.glow, to.glow) * dim * (wide ? 1 : 0.45));
        if (pointer.active) gl.uniform2f(uniform('uPointer'), pointer.x, pointer.y);
        else gl.uniform2f(uniform('uPointer'), 9, 9);

        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.POINTS, 0, count);
    }

    requestAnimationFrame(frame);

    return { measure };
}
